import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {
  DashboardKpi,
  DashboardMeeting,
  DashboardNeedItem,
  DashboardOrgProgress,
  DashboardPeopleOrg,
  DashboardSearchHit,
  DashboardSummary,
  DashboardTodoCounts,
} from '@todo/shared';
import { dashboardApi } from '../api/dashboard.api';
import { ModuleIcon } from '../components/ModuleIcon';
import { MODULE_BRAND_ICON } from '../lib/moduleIcons';
import type { ModuleId } from '../lib/modules';
import { Button } from '../components/ui/Button';
import { Dropdown } from '../components/ui/Dropdown';
import { MemberReport } from '../components/MemberReport';
import { memberReportApi } from '../api/member-report.api';

/**
 * The Dashboard module - built from the "Charles Workspace" dashboard design.
 *
 * ONE request fills the page (GET /api/dashboard). The server decides which
 * panels exist for this person: `people` and `audit` arrive null for anyone
 * who may not see them and the panel is simply absent. The footer, rail and
 * top bar are the layout's, untouched here.
 *
 * Colour means an APP (one accent each), state colours are a separate set,
 * and blue is the only filled button - see index.css `--dash-*`.
 */

type App = 'todo' | 'sign' | 'meet' | 'vault' | 'people' | 'audit';

const TEXT: Record<App, string> = {
  todo: 'text-dash-todo', sign: 'text-dash-sign', meet: 'text-dash-meet',
  vault: 'text-dash-vault', people: 'text-dash-people', audit: 'text-dash-audit',
};
const TINT: Record<App, string> = {
  todo: 'dash-tint-todo', sign: 'dash-tint-sign', meet: 'dash-tint-meet',
  vault: 'dash-tint-vault', people: 'dash-tint-people', audit: 'dash-tint-audit',
};
const EDGE: Record<App, string> = {
  todo: 'dash-edge-todo', sign: 'dash-edge-sign', meet: 'dash-edge-meet',
  vault: 'dash-edge-vault', people: 'dash-edge-people', audit: 'dash-edge-audit',
};
const BADGE: Record<App, string> = {
  todo: 'TODO', sign: 'Sign', meet: 'Meeting', vault: 'Vault', people: 'People', audit: 'Audit',
};

// ----------------------------------------------------------- formatting ----

const IST = 'Asia/Kolkata';

function greeting(now: Date): string {
  const h = Number(
    new Intl.DateTimeFormat('en-GB', { timeZone: IST, hour: '2-digit', hourCycle: 'h23' }).format(now),
  );
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

/** "Friday 9 October" - no year on today's screen. */
const fmtToday = (d: Date) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: IST, weekday: 'long', day: 'numeric', month: 'long' }).format(d);

/** 24-hour clock: "09:30". */
const fmtClock = (iso: string) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: IST, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .format(new Date(iso));

const fmtWeekday = (iso: string) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: IST, weekday: 'short' }).format(new Date(iso));

const fmtShortDate = (iso: string) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: IST, day: 'numeric', month: 'short' }).format(new Date(iso));

const dayKey = (iso: string | Date) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: IST }).format(new Date(iso));

/** Above 999 the figure becomes 1.2k; the full number rides in `title`. */
function compact(n: number): string {
  return n > 999 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(n);
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';

// -------------------------------------------------------------- pieces -----

/** Each dashboard app is one of the module rail's modules; People lives
 *  under Creator. The BRAND marks (lib/moduleIcons) are used so a panel, a
 *  tile and the rail all draw the same module with the same icon. */
const APP_MODULE: Record<App, ModuleId> = {
  todo: 'todo', sign: 'sign', meet: 'meetings', vault: 'vault', people: 'creator', audit: 'audit',
};

function Plate({ app, className = 'h-8 w-8' }: { app: App; className?: string }) {
  const Icon = MODULE_BRAND_ICON[APP_MODULE[app]];
  return (
    <span className={`grid shrink-0 place-items-center rounded-md ${className} ${TINT[app]}`}>
      <Icon className="h-full w-full p-0.5" />
    </span>
  );
}

function Panel({
  app, title, count, children, className = '', plate,
}: {
  app: App; title: string; count?: ReactNode; children: ReactNode; className?: string;
  /** Replaces the app's own icon plate; null = no icon (a panel that spans every app). */
  plate?: ReactNode;
}) {
  return (
    <section className={`flex h-full flex-col rounded-2xl border border-dash-line bg-dash-raised p-4 shadow-sm md:p-5 ${className}`}>
      <header className="mb-4 flex items-center gap-2">
        {plate === undefined ? <Plate app={app} /> : plate}
        <h2 className="text-[17px] font-semibold leading-[22px] text-dash-ink">{title}</h2>
        {count !== undefined && <span className="ml-auto text-[13px] text-dash-muted">{count}</span>}
      </header>
      {children}
    </section>
  );
}

function Quiet({ children }: { children: ReactNode }) {
  return <p className="py-3 text-center text-[13px] text-dash-muted">{children}</p>;
}

/** The panel's footer action: the shared Button, pinned to the bottom-left. */
function TextLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <div className="mt-auto pt-4">
      <Button to={to} variant="secondary" size="sm">{children}</Button>
    </div>
  );
}

function Row({ children, to }: { children: ReactNode; to?: string }) {
  const cls = 'flex items-center gap-3 rounded-xl bg-dash-row p-3 md:p-4';
  return to
    ? <Link to={to} className={`${cls} dash-focus border border-transparent hover:border-dash-line-strong`}>{children}</Link>
    : <div className={cls}>{children}</div>;
}

function RowText({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold text-dash-ink" title={title}>{title}</p>
      <p className="line-clamp-2 text-[13px] text-dash-muted">{detail}</p>
    </div>
  );
}

function Skeleton({ className }: { className: string }) {
  return <div className={`animate-pulse rounded-xl bg-dash-row ${className}`} />;
}

// -------------------------------------------------------------- meter ------

/** Fixed order - not started, in progress, blocked, done - so the SHAPE of
 *  the bar reads without the legend. A zero segment draws nothing. */
function Meter({ c }: { c: DashboardTodoCounts }) {
  const seg = [
    { n: c.notStarted, cls: 'bg-dash-neutral', label: 'Not started', dot: 'bg-dash-neutral' },
    { n: c.inProgress, cls: 'bg-dash-warn', label: 'In progress', dot: 'bg-dash-warn' },
    { n: c.blocked, cls: 'bg-dash-danger', label: 'Blocked', dot: 'bg-dash-danger' },
    { n: c.completed, cls: 'bg-dash-ok', label: 'Done', dot: 'bg-dash-ok' },
  ];
  return (
    <>
      <div className="flex h-2 overflow-hidden rounded-full bg-dash-row" role="img"
        aria-label={`${c.completed} of ${c.total} done`}>
        {seg.filter((s) => s.n > 0).map((s) => (
          <div key={s.label} className={`dash-seg h-full ${s.cls}`} style={{ width: `${(s.n / c.total) * 100}%` }} />
        ))}
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[13px] text-dash-ink2">
        {seg.map((s) => (
          <li key={s.label} className="flex items-center gap-1.5">
            <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
            <span className="tabular-nums font-semibold text-dash-ink">{s.n}</span> {s.label}
          </li>
        ))}
      </ul>
    </>
  );
}

// ------------------------------------------------------------- search ------

const HIT_LABEL: Record<DashboardSearchHit['kind'], string> = {
  TASK: 'Tasks', SIGN: 'CG Sign', MEETING: 'Calendar', FILE: 'Vault', PERSON: 'People',
};

function SearchBox() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<DashboardSearchHit[] | null>(null);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits(null); return; }
    let live = true;
    const t = setTimeout(() => {
      dashboardApi.search(term)
        .then((r) => { if (live) { setHits(r); setOpen(true); } })
        .catch(() => { if (live) setHits([]); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);

  useEffect(() => {
    function away(e: PointerEvent) {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, []);

  const grouped = useMemo(() => {
    const g = new Map<string, DashboardSearchHit[]>();
    for (const h of hits ?? []) g.set(h.kind, [...(g.get(h.kind) ?? []), h]);
    return [...g.entries()] as [DashboardSearchHit['kind'], DashboardSearchHit[]][];
  }, [hits]);

  return (
    <div ref={box} className="relative w-full md:w-[420px]">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-dash-muted">
        <ModuleIcon name="search" className="h-4 w-4" />
      </span>
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => hits && setOpen(true)}
        placeholder="Search everything"
        aria-label="Search everything"
        className="dash-focus h-10 w-full rounded-lg border border-dash-line bg-dash-field pl-9 pr-3 text-sm text-dash-ink placeholder:text-dash-muted"
      />
      {open && hits && (
        <div className="absolute z-30 mt-1 max-h-96 w-full overflow-y-auto rounded-xl border border-dash-line bg-dash-raised p-1 shadow-[0_12px_32px_rgba(0,0,0,0.35)]">
          {grouped.length === 0 ? (
            <p className="px-3 py-3 text-[13px] text-dash-muted">Nothing found for “{q.trim()}”.</p>
          ) : grouped.map(([kind, rows]) => (
            <div key={kind}>
              <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-dash-muted">
                {HIT_LABEL[kind]}
              </p>
              {rows.map((h) => (
                <button key={`${h.kind}${h.id}`} type="button"
                  onClick={() => { setOpen(false); navigate(h.to); }}
                  className="dash-focus block w-full rounded-md px-3 py-1.5 text-left hover:bg-dash-row">
                  <span className="block truncate text-sm text-dash-ink">{h.title}</span>
                  <span className="block truncate text-[13px] text-dash-muted">{h.subtitle}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------- stat tiles ------

const KPI_APP: Record<DashboardKpi['key'], App> = {
  openTasks: 'todo', toSign: 'sign', meetingsToday: 'meet', files: 'vault',
  people: 'people', overdue: 'todo', eventsToday: 'audit', rsvpPending: 'meet',
};
/** The first three tiles carry an accent border WHEN they have something waiting. */
const KPI_WAITING = new Set<DashboardKpi['key']>(['openTasks', 'toSign', 'meetingsToday']);

/**
 * The Member Report section, for the one organisation it is switched on for
 * (and only its ADMIN / SUPER_ADMIN). The SERVER decides (organisations.industry =
 * 'Political' and the role) and the section's own endpoints
 * enforce the same rule, so hiding it here is a convenience, not the fence.
 * Re-asked every time the Dashboard is (re)loaded, which includes switching
 * organisation.
 */
function MemberReportGate({ loadedAt }: { loadedAt: string }) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    setEnabled(false);
    memberReportApi.access()
      .then((v) => { if (live) setEnabled(v); })
      .catch(() => { /* off: a failed check must never block the Dashboard */ });
    return () => { live = false; };
  }, [loadedAt]);
  return enabled ? <MemberReport /> : null;
}

function StatTile({ k }: { k: DashboardKpi }) {
  const app = KPI_APP[k.key];
  const waiting = KPI_WAITING.has(k.key) && k.value > 0;
  return (
    <Link to={k.to}
      className={`dash-focus flex items-center gap-3 rounded-xl border bg-dash-raised p-4 ${waiting ? EDGE[app] : 'border-dash-line'} hover:border-dash-line-strong`}>
      <Plate app={app} className="h-10 w-10" />
      <div className="min-w-0">
        <p className="text-[26px] font-bold leading-[30px] tracking-tight text-dash-ink tabular-nums" title={String(k.value)}>
          {compact(k.value)}
        </p>
        <p className="truncate text-[13px] font-medium capitalize text-dash-muted">{k.label}</p>
      </div>
    </Link>
  );
}

// ------------------------------------------------------- needs you now -----

const NEED_APP: Record<DashboardNeedItem['kind'], App> = {
  SIGN: 'sign', RSVP: 'meet', TASK_OVERDUE: 'todo', TASK_DUE_TODAY: 'todo',
};
const SHOWN = 3;

function NeedsYou({ items, total }: { items: DashboardNeedItem[]; total: number }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, SHOWN);
  const more = Math.max(0, total - SHOWN);
  return (
    <Panel app="todo" plate={null} title="Needs you now" count={`${total} across all apps`} className="order-1 xl:order-none">
      <ul className="max-h-[26rem] space-y-3 overflow-y-auto pr-1">
        {shown.map((n) => {
          const app = NEED_APP[n.kind];
          return (
            <li key={`${n.kind}${n.id}`}>
              <Row>
                <Plate app={app} className="h-10 w-10" />
                <RowText title={n.title} detail={n.subtitle} />
                <span className={`hidden h-6 shrink-0 items-center rounded-md border px-2 text-[11px] font-semibold tracking-wide sm:inline-flex ${EDGE[app]} ${TEXT[app]}`}>
                  {BADGE[app]}
                </span>
                <Link to={n.to}
                  className="dash-focus inline-flex h-9 shrink-0 items-center rounded-lg bg-dash-action px-4 text-[13px] font-semibold text-white hover:bg-dash-action-hover">
                  {n.action}
                </Link>
              </Row>
            </li>
          );
        })}
      </ul>
      {more > 0 && items.length > SHOWN && (
        <button type="button" onClick={() => setAll((v) => !v)}
          className="dash-focus mt-3 rounded text-[13px] font-medium text-dash-action-text hover:underline">
          {all ? 'Show fewer' : `+${more} more`}
        </button>
      )}
    </Panel>
  );
}

// ------------------------------------------------------------- to-do -------

function TodoPanel({ orgs }: { orgs: DashboardOrgProgress[] }) {
  // Closed until clicked: the card answers "how is the organisation doing"
  // first; departments are the drill-down.
  const [openOrg, setOpenOrg] = useState<string | null>(null);
  const multi = orgs.length > 1;
  return (
    <Panel app="todo" title="To-Do" count={multi ? `${orgs.length} organisations` : undefined} className="order-2 xl:order-none xl:col-span-4">
      {orgs.length === 0 ? <Quiet>No organisation to show.</Quiet> : (
        <ul className="max-h-[26rem] space-y-3 overflow-y-auto pr-1">
          {orgs.map((o) => {
            const open = openOrg === o.orgId;
            return (
              <li key={o.orgId} className="rounded-xl bg-dash-row">
                <button type="button" aria-expanded={open} onClick={() => setOpenOrg(open ? null : o.orgId)}
                  className="dash-focus block w-full rounded-xl p-4 text-left">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-[15px] font-semibold text-dash-ink">{o.orgName}</p>
                      <p className="text-[13px] text-dash-muted">
                        {o.total} tasks · {o.percent}% done
                        {o.overdue > 0 && <> · <span className="font-semibold text-dash-danger">{o.overdue} overdue</span></>}
                      </p>
                    </div>
                    <svg viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8"
                      className={`mt-1 h-4 w-4 shrink-0 text-dash-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
                      <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="mt-3">
                    {o.total === 0 ? <p className="text-[13px] text-dash-muted">No tasks yet</p> : <Meter c={o} />}
                  </div>
                </button>
                {open && (
                  <ul className="space-y-4 border-t border-dash-line-strong p-4">
                    {o.departments.length === 0 && <li className="text-[13px] text-dash-muted">No departments with tasks.</li>}
                    {o.departments.map((d) => (
                      <li key={d.departmentId ?? 'none'}>
                        <p className="mb-1.5 flex items-baseline justify-between gap-2 text-sm">
                          <span className="truncate font-semibold text-dash-ink">{d.departmentName}</span>
                          <span className="shrink-0 text-[13px] text-dash-muted">{d.total} tasks · {d.percent}% done</span>
                        </p>
                        <Meter c={d} />
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

// -------------------------------------------------------------- sign -------

const SIGN_STATUS: Record<string, string> = {
  SENT: 'Out for signature', COMPLETED: 'Completed', DECLINED: 'Declined', VOIDED: 'Voided',
};

function SignPanel({ s }: { s: DashboardSummary['sign'] }) {
  const total = s.waiting + s.completed + s.declined + s.voided;
  const metrics = [
    { n: s.waiting, label: 'waiting', tone: 'text-dash-warn' },
    { n: s.completed, label: 'completed', tone: 'text-dash-ok' },
    { n: s.declined, label: 'declined', tone: 'text-dash-danger' },
    { n: s.voided, label: 'voided', tone: 'text-dash-ink2' },
  ];
  return (
    <Panel app="sign" title="Sign" count={`${total} ${total === 1 ? 'document' : 'documents'}`} className="order-3 xl:order-none">
      <div className="mb-4 grid grid-cols-4 gap-2">
        {metrics.map((m) => (
          <div key={m.label}>
            <p className={`text-xl font-bold leading-[26px] tabular-nums ${m.tone}`}>{m.n}</p>
            <p className="text-[13px] text-dash-muted">{m.label}</p>
          </div>
        ))}
      </div>
      {s.recent.length === 0 ? <Quiet>No documents yet.</Quiet> : (
        <ul className="space-y-3">
          {s.recent.slice(0, 2).map((d) => (
            <li key={d.id}>
              <Row to="/cg-sign">
                <RowText title={d.title}
                  detail={`${SIGN_STATUS[d.status] ?? d.status} · ${d.signed} of ${d.recipients} · ${fmtShortDate(d.createdAt)}`} />
              </Row>
            </li>
          ))}
        </ul>
      )}
      <TextLink to="/cg-sign">Open CG Sign</TextLink>
    </Panel>
  );
}

// ---------------------------------------------------------- meetings -------

function MeetingsPanel({ m }: { m: DashboardSummary['meetings'] }) {
  const todayKey = dayKey(new Date());
  // Today's meetings first, then what is coming - the design shows both.
  const rows: DashboardMeeting[] = [
    ...m.today,
    ...m.upcoming.filter((e) => dayKey(e.startsAt) !== todayKey),
  ].slice(0, 3);
  const people = (e: DashboardMeeting) => e.attendeeCount > 0 ? `${e.attendeeCount + 1} people` : 'Just you';
  return (
    <Panel app="meet" title="Calendar" count={`${m.today.length} today · ${m.weekCount} this week`} className="order-4 xl:order-none">
      {rows.length === 0 ? <Quiet>No meetings this week.</Quiet> : (
        <ul className="space-y-3">
          {rows.map((e) => {
            const isToday = dayKey(e.startsAt) === todayKey;
            const when = isToday
              ? `${fmtClock(e.startsAt)} – ${fmtClock(e.endsAt)}`
              : `${fmtWeekday(e.startsAt)} ${fmtClock(e.startsAt)}`;
            return (
              <li key={e.id}>
                <Row to="/calendar">
                  <RowText title={e.title}
                    detail={`${when} · ${e.location ?? people(e)}${e.response === 'PENDING' ? ' · RSVP needed' : ''}`} />
                </Row>
              </li>
            );
          })}
        </ul>
      )}
      <TextLink to="/calendar">Open calendar</TextLink>
    </Panel>
  );
}

// ------------------------------------------------------------ people -------

function PeoplePanel({ orgs }: { orgs: DashboardPeopleOrg[] }) {
  const [orgId, setOrgId] = useState(orgs[0]?.orgId ?? '');
  const [openDept, setOpenDept] = useState<string | null>(null);
  const org = orgs.find((o) => o.orgId === orgId) ?? orgs[0];
  if (!org) return null;
  const max = Math.max(1, ...org.departments.map((d) => d.count));
  return (
    <Panel app="people" title="People" count={`${org.total} ${org.total === 1 ? 'person' : 'people'}`} className="order-5 xl:order-none xl:col-span-4">
      {orgs.length > 1 ? (
        <div className="mb-4">
          <Dropdown
            value={org.orgId}
            onChange={(v) => { setOrgId(v); setOpenDept(null); }}
            searchable={false}
            options={orgs.map((o) => ({ value: o.orgId, label: o.orgName, description: `${o.total} people` }))}
          />
        </div>
      ) : (
        <p className="mb-3 text-[15px] font-semibold text-dash-ink">{org.orgName}</p>
      )}
      {org.departments.length === 0 ? <Quiet>No people yet.</Quiet> : (
        <ul className="max-h-[18rem] space-y-3 overflow-y-auto pr-1">
          {org.departments.map((d) => {
            const key = d.departmentId ?? '_none';
            const open = openDept === key;
            return (
              <li key={key} className="rounded-xl bg-dash-row">
                <button type="button" aria-expanded={open} onClick={() => setOpenDept(open ? null : key)}
                  className="dash-focus flex w-full items-center gap-3 rounded-xl p-3 text-left">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-semibold dash-tint-people text-dash-people">
                    {initials(d.departmentName)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-dash-ink">{d.departmentName}</span>
                    <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-dash-field">
                      <span className="block h-full rounded-full bg-dash-people" style={{ width: `${(d.count / max) * 100}%` }} />
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-dash-ink">{d.count}</span>
                </button>
                {open && (
                  <ul className="max-h-48 divide-y divide-dash-line overflow-y-auto border-t border-dash-line-strong px-3">
                    {d.members.map((p) => (
                      <li key={p.id} className="py-1.5">
                        <p className="text-sm text-dash-ink">{p.displayName}</p>
                        {p.jobTitle && <p className="text-[13px] text-dash-muted">{p.jobTitle}</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <TextLink to="/people">Manage people</TextLink>
    </Panel>
  );
}

// ------------------------------------------------------------- vault -------

function fileChip(name: string): { ext: string; cls: string } {
  const ext = (name.split('.').pop() ?? '').toUpperCase().slice(0, 4);
  if (ext === 'PDF') return { ext, cls: 'text-dash-vault' };
  if (ext === 'XLS' || ext === 'XLSX' || ext === 'CSV') return { ext: ext.slice(0, 3), cls: 'text-dash-ok' };
  if (ext === 'DOC' || ext === 'DOCX') return { ext: ext.slice(0, 3), cls: 'text-dash-todo' };
  return { ext: ext || 'FILE', cls: 'text-dash-ink2' };
}

function VaultPanel({ v }: { v: DashboardSummary['vault'] }) {
  const todayKey = dayKey(new Date());
  return (
    <Panel app="vault" title="Vault" count={`${v.files} files · ${v.sharedWithMe} shared`} className="order-6 xl:order-none xl:col-span-4">
      {v.recent.length === 0 ? <Quiet>No files yet.</Quiet> : (
        <ul className="space-y-3">
          {v.recent.slice(0, 2).map((f) => {
            const chip = fileChip(f.fileName);
            const day = dayKey(f.createdAt) === todayKey ? 'today' : fmtShortDate(f.createdAt);
            const who = f.isMine ? (f.shared ? 'shared' : 'private') : `from ${f.ownerName}`;
            return (
              <li key={f.id}>
                <Row to="/vault">
                  <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-md bg-dash-field text-[10px] font-bold ${chip.cls}`}>
                    {chip.ext}
                  </span>
                  <RowText title={f.fileName} detail={`${fmtBytes(f.sizeBytes)} · ${who} · ${day}`} />
                </Row>
              </li>
            );
          })}
        </ul>
      )}
      <TextLink to="/vault">Open Vault</TextLink>
    </Panel>
  );
}

// ------------------------------------------------------------- audit -------

/** change_log verbs -> words (the Audit Trail page keeps the full list). */
const VERB: Record<string, string> = {
  CREATED: 'created', UPDATED: 'edited', DELETED: 'deleted',
  STATUS_CHANGED: 'changed the status of', SHARED: 'shared', UNSHARED: 'revoked a share on',
  COMMENTED: 'commented on', AI_CHAT: 'ran via AI Chat:',
};

function AuditPanel({ a }: { a: NonNullable<DashboardSummary['audit']> }) {
  const more = Math.max(0, a.todayCount - SHOWN);
  return (
    <Panel app="audit" title="Audit trail" count={`${a.todayCount} events today`} className="order-7 xl:order-none xl:col-span-4">
      {a.events.length === 0 ? <Quiet>No activity yet.</Quiet> : (
        <ul className="space-y-3">
          {a.events.slice(0, SHOWN).map((e, i) => (
            <li key={i}>
              <Row>
                <p className="min-w-0 flex-1 truncate text-sm text-dash-ink2">
                  <span className="font-semibold text-dash-ink">{e.actorName ?? 'System'}</span>{' '}
                  {VERB[e.verb] ?? e.verb.toLowerCase().replace(/_/g, ' ')}{' '}
                  <span className="text-dash-ink">{e.entityLabel ?? e.entityType}</span>
                </p>
                <time className="shrink-0 font-mono text-[13px] font-medium text-dash-muted"
                  dateTime={e.createdAt} title={`${fmtShortDate(e.createdAt)} ${fmtClock(e.createdAt)}`}>
                  {fmtClock(e.createdAt)}
                </time>
              </Row>
            </li>
          ))}
        </ul>
      )}
      {more > 0 ? <TextLink to="/audit">{`+${more} more`}</TextLink> : <TextLink to="/audit">Open Audit Trail</TextLink>}
    </Panel>
  );
}

// -------------------------------------------------------------- page -------

export default function DashboardPage() {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setError(null);
    dashboardApi.get().then(setData).catch(() => setError('Could not load the dashboard.'));
  }
  useEffect(load, []);

  const now = new Date();
  const first = data?.viewer.displayName.split(' ')[0] ?? '';

  return (
    <div className="min-h-full bg-dash-base font-sans text-dash-ink antialiased">
      <div className="mx-auto max-w-[1760px] p-4 md:p-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[15px] font-semibold text-dash-ink">
              {greeting(now)}{first ? `, ${first}` : ''}
            </p>
            <p className="text-[13px] text-dash-muted">{fmtToday(now)} · everything across your apps</p>
          </div>
          <SearchBox />
        </div>

        {error ? (
          <div className="rounded-2xl border border-dash-danger/40 bg-dash-raised p-5">
            <p className="text-sm text-dash-danger">{error}</p>
            <button type="button" onClick={load}
              className="dash-focus mt-2 rounded text-[13px] font-medium text-dash-action-text hover:underline">
              Retry
            </button>
          </div>
        ) : !data ? (
          <>
            <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-3 2xl:grid-cols-6">
              {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-[74px]" />)}
            </div>
            <div className="grid gap-4 xl:grid-cols-12">
              <Skeleton className="h-56 xl:col-span-8" />
              <Skeleton className="h-56 xl:col-span-4" />
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-3 2xl:grid-cols-6">
              {data.kpis.map((k) => <StatTile key={k.key} k={k} />)}
            </div>

            <MemberReportGate loadedAt={data.generatedAt} />

            {/*
              Row 1: Needs you now + Sign + Meetings stack on the left and
              stretch to the height of To-Do on the right. Row 2: People,
              Vault and Audit trail share a height. Below xl the wrapper
              vanishes (`contents`) and `order-*` restores a single column.
            */}
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12 xl:items-stretch">
              <div className="contents xl:col-span-8 xl:flex xl:flex-col xl:gap-4">
                {/* Nothing waiting: the panel is hidden, not shown empty. */}
                {data.needsYou.length > 0 && <NeedsYou items={data.needsYou} total={data.needsYouCount} />}
                <div className="contents xl:grid xl:flex-1 xl:grid-cols-2 xl:items-stretch xl:gap-4">
                  <SignPanel s={data.sign} />
                  <MeetingsPanel m={data.meetings} />
                </div>
              </div>
              <TodoPanel orgs={data.todo} />
              {data.people && <PeoplePanel orgs={data.people} />}
              <VaultPanel v={data.vault} />
              {data.audit && <AuditPanel a={data.audit} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
